import React, { Component, ReactNode } from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  padding: 20px;
  background-color: rgba(255, 243, 205, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  margin: 10px;
  backdrop-filter: blur(10px);
`;

const ErrorTitle = styled.h3`
  color: #ffc107;
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 10px 0;
  font-size: 13px;
  line-height: 1.4;
`;

const ErrorButton = styled.button`
  background-color: rgba(255, 193, 7, 0.2);
  border: 1px solid rgba(255, 193, 7, 0.4);
  color: #ffc107;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 193, 7, 0.3);
    border-color: rgba(255, 193, 7, 0.6);
  }
`;

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
  sectionName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Error Boundary pour protéger les sections critiques de l'application
 * 
 * @component
 * @example
 * ```tsx
 * <SectionErrorBoundary 
 *   sectionName="Zones de prospection"
 *   fallbackMessage="Impossible de charger les zones"
 * >
 *   <ZonesSection />
 * </SectionErrorBoundary>
 * ```
 */
export class SectionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Mettre à jour l'état pour afficher l'UI de fallback au prochain rendu
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Logger l'erreur pour le debugging
    console.error('❌ Error Boundary caught an error:', {
      section: this.props.sectionName || 'Unknown section',
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Stocker les infos d'erreur dans le state
    this.setState({
      errorInfo
    });
  }

  handleReload = () => {
    // Recharger la page pour réinitialiser l'état
    window.location.reload();
  };

  handleReset = () => {
    // Réinitialiser l'état de l'error boundary
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      const { sectionName, fallbackMessage } = this.props;
      const { error } = this.state;

      return (
        <ErrorContainer>
          <ErrorTitle>
            ⚠️ {sectionName ? `Erreur dans ${sectionName}` : 'Une erreur est survenue'}
          </ErrorTitle>
          <ErrorMessage>
            {fallbackMessage || 
             'Cette section a rencontré un problème. Vous pouvez essayer de recharger la page.'}
          </ErrorMessage>
          {process.env.NODE_ENV === 'development' && error && (
            <ErrorMessage style={{ 
              fontSize: '11px', 
              color: 'rgba(255, 255, 255, 0.6)',
              fontFamily: 'monospace',
              marginTop: '10px',
              padding: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              maxHeight: '100px',
              overflow: 'auto'
            }}>
              <strong>Détails (dev) :</strong><br />
              {error.message}
            </ErrorMessage>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <ErrorButton onClick={this.handleReset}>
              Réessayer
            </ErrorButton>
            <ErrorButton onClick={this.handleReload}>
              Recharger la page
            </ErrorButton>
          </div>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default SectionErrorBoundary;
